export class UploadHttpError extends Error {
  constructor(public readonly status: number) {
    super(`upload failed with status ${status}`)
  }
}

export class UploadAbortedError extends Error {
  constructor() {
    super('upload aborted')
  }
}

export interface UploadTarget {
  method: string
  url: string
  headers: Record<string, string>
}

/**
 * PUTs a blob straight to a presigned URL with a plain XMLHttpRequest. It deliberately does
 * not use the shared axios client: no cookies, no XSRF header, and exactly the headers from
 * the ticket (nothing added, nothing dropped).
 */
export function uploadToPresignedUrl(
  blob: Blob,
  target: UploadTarget,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError())
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open(target.method, target.url, true)
    xhr.withCredentials = false
    for (const [name, value] of Object.entries(target.headers)) {
      xhr.setRequestHeader(name, value)
    }

    const onAbort = (): void => xhr.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    const cleanup = (): void => signal?.removeEventListener('abort', onAbort)

    xhr.upload.onprogress = (event: ProgressEvent): void => {
      if (event.lengthComputable) onProgress(event.loaded, event.total)
    }
    xhr.onload = (): void => {
      cleanup()
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(blob.size, blob.size)
        resolve()
      } else {
        reject(new UploadHttpError(xhr.status))
      }
    }
    xhr.onerror = (): void => {
      cleanup()
      reject(new UploadHttpError(0))
    }
    xhr.ontimeout = xhr.onerror
    xhr.onabort = (): void => {
      cleanup()
      reject(new UploadAbortedError())
    }

    xhr.send(blob)
  })
}
